import { IconChevronLeft } from '@aws-amplify/ui-react/internal'
import { Box } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import ButtonBar from '@/components/inputs/ButtonBar'
import CustomButton from '@/components/inputs/CustomButton'
import PageHeader from '@/components/layouts/PageHeader'
import BidOverviewButton from '@/features/bidding/components/BidOverviewButton'
import BidOfferPriceByActivationGroupTabs from '@/features/bidding/components/capacity/offer/BidOfferPriceByActivationGroupTabs'
import type { ActivationGroupBidUpdate } from '@/features/bidding/components/capacity/offer/PriceDataGridForActivationGroup'
import CountrySelector from '@/features/bidding/components/CountrySelector'
import CreateBidButton from '@/features/bidding/components/CreateBidButton'
import OfferedPriceVolumeInvalidTooltip from '@/features/bidding/components/OfferedPriceVolumeInvalidTooltip'
import { useBidOfferPriceActions_ByActivationGroup } from '@/features/bidding/hooks/useBidOfferPriceActions_ByActivationGroup'
import { useBidOfferPriceTabIcons } from '@/features/bidding/hooks/useBidOfferPriceTabIcons'
import { useShouldShowFinlandWarning } from '@/features/bidding/hooks/useShouldShowFinlandWarning'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidHistoriesPage } from '@/features/bidding/types/bidHistory'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { buildBidCapacityLink } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import { withRevalidatedStates } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'

type Props = {
  bidHistoriesPage: BidHistoriesPage | undefined
  bidsWithPrices: ExtendedBid[]
  groupParams: GroupedBidParams
  initialBidsPerActivationGroup: Map<string, AggregatedBidWrapper>
  latestNonDraftBidsByActivationGroup: Map<string, ExtendedBid | null>
  latestAcceptedBidsByActivationGroup: Map<string, ExtendedBid | null>
}

const BidOfferPriceByActivationGroup = ({
  bidHistoriesPage,
  bidsWithPrices,
  groupParams,
  initialBidsPerActivationGroup,
  latestNonDraftBidsByActivationGroup,
  latestAcceptedBidsByActivationGroup,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const actions = useBidOfferPriceActions_ByActivationGroup({ groupParams })

  const [bidsPerActivationGroup, setBidsPerActivationGroup] =
    useState<Map<string, AggregatedBidWrapper>>(initialBidsPerActivationGroup)

  // Calculate tab icons based on current bid state (updates reactively as user edits)
  const tabIcons = useBidOfferPriceTabIcons(bidsPerActivationGroup)

  // Check if Finland warning should be shown (updates reactively as user edits)
  const shouldShowFinlandWarning = useShouldShowFinlandWarning(bidsPerActivationGroup, groupParams)

  const onSubmit = async () => {
    const validationResult = actions.validate({
      bidsPerActivationGroup,
      bidsWithPrices,
    })

    if (!validationResult.isValid) {
      // Validation errors are already checked by submitEnabled
      // This is an extra safety check
      return
    }

    await actions.save({
      bidsPerActivationGroup,
      bidsWithPrices,
    })
  }

  const onBidForActivationGroupUpdated = ({ activationGroup, aggregatedBid }: ActivationGroupBidUpdate) => {
    const existingBid = bidsPerActivationGroup.get(activationGroup.uuid)
    if (!existingBid) {
      throw new Error(`No existing bid found for activation group ${activationGroup.uuid}`)
    }

    const updatedBids = new Map(bidsPerActivationGroup).set(activationGroup.uuid, { ...existingBid, aggregatedBid })
    setBidsPerActivationGroup(withRevalidatedStates(updatedBids, groupParams.bidType))
  }

  const submitEnabled = actions.validate({
    bidsPerActivationGroup,
    bidsWithPrices,
  }).isValid

  return (
    <>
      <PageHeader
        appBarContent={
          <Box alignItems="center" display="flex" flexDirection="row" justifyContent="space-between" width="100%">
            <CountrySelector disabled />
            <BidOverviewButton />
          </Box>
        }
        breadcrumbItems={[
          { text: t('component.page_header.bidding'), to: '/bidding' },
          { text: t('component.page_header.bid.todo_list'), to: '/bidding/todo-list' },
          {
            text: t('component.page_header.bid.create.label', {
              portfolio: groupParams.portfolioCode,
              marketProgram: t(`bidding.market_program.${groupParams.marketProgram}`),
              bidType: groupParams.bidType.toLowerCase(),
            }),
            to: buildBidCapacityLink(groupParams),
          },
        ]}
        pageTitle={t('bidding.create_bid.price_bid')}
      />

      {bidsPerActivationGroup.size > 0 && (
        <BidOfferPriceByActivationGroupTabs
          bidHistoriesPage={bidHistoriesPage}
          bidsPerActivationGroup={bidsPerActivationGroup}
          groupParams={groupParams}
          latestAcceptedBidsByActivationGroup={latestAcceptedBidsByActivationGroup}
          latestNonDraftBidsByActivationGroup={latestNonDraftBidsByActivationGroup}
          tabIcons={tabIcons}
          onBidForActivationGroupUpdated={onBidForActivationGroupUpdated}
        />
      )}

      <ButtonBar>
        <CustomButton
          key="backButton"
          startIcon={<IconChevronLeft />}
          variant={'outlined'}
          onClick={() => navigate(-1)}
        >
          {t('common.navigation.back')}
        </CustomButton>
        <OfferedPriceVolumeInvalidTooltip disabled={submitEnabled}>
          <CreateBidButton
            dialogProps={{
              description: t('bidding.dialog.finland_mfrr_energy_pricing_limitation.description'),
              title: t('bidding.dialog.finland_mfrr_energy_pricing_limitation.title'),
            }}
            disabled={!submitEnabled || actions.isSaving}
            shouldShowConfirmation={shouldShowFinlandWarning}
            onSubmit={onSubmit}
          />
        </OfferedPriceVolumeInvalidTooltip>
      </ButtonBar>
    </>
  )
}

export default BidOfferPriceByActivationGroup
